import type { EstimateTotals } from '../services/estimateMath';

/**
 * utils/formatMargin.ts — Cómo se ESCRIBE un margen. Una sola vez, para todos.
 *
 * POR QUÉ EXISTE:
 *   `services/estimateMath.ts` ya garantiza que todas las pantallas CALCULEN el
 *   mismo margen. Lo que no garantizaba es que lo PINTEN igual, y no lo hacían:
 *
 *     components/EstimateCalculator.tsx   `.toFixed(0)`  →  "30%"
 *     components/EstimateDetail.tsx       `.toFixed(1)`  →  "29.6%"
 *     views/EstimatesView.tsx             `.toFixed(1)`  →  "29.6%"
 *
 *   El mismo presupuesto decía 30% o 29,6% según por dónde se entrara — que es
 *   una versión más pequeña del fallo que aquel módulo existía para eliminar.
 *
 *   Y ahora hay DOS márgenes que enseñar (Parte A.4 del plan v2): el del
 *   TRABAJO PROPIO —el que persigue el dial, sin los pase-directo— y el
 *   FACTURADO —todo dentro—. Dos números por tres pantallas son seis formas de
 *   equivocarse. Por eso el formato vive aquí y no en cada `.tsx`.
 *
 * LA REGLA:
 *   · Un decimal, siempre. `toFixed(0)` convertía 42,86% en "43%" —el número
 *     que el contratista no reconocía— y 29,5% en un "30%" que parecía cumplir
 *     la meta. El decimal es barato y no miente.
 *   · El trabajo propio va PRIMERO: es la métrica sobre la que se decide.
 *   · El facturado solo se menciona cuando difiere, o sea cuando hay
 *     pase-directo. Enseñar "30,0% · 30,0%" no informa de nada y entrena a la
 *     gente a ignorar el segundo número.
 */

/** Un porcentaje con un decimal. El único formateador de porcentajes de margen. */
export function formatMarginPercent(value: number | null | undefined): string {
    const n = Number.isFinite(value as number) ? (value as number) : 0;
    return `${n.toFixed(1)}%`;
}

/** Las dos lecturas de un estimado, ya escritas. */
export interface MarginPairText {
    /** El margen del trabajo propio, p. ej. "30,0%". Es el que manda. */
    own: string;
    /** El facturado (pase-directo incluido), p. ej. "28,0%". */
    billed: string;
    /** ¿Difieren? Solo entonces vale la pena enseñar los dos. */
    differs: boolean;
    /** Cuánto pase-directo hay, en dólares — para explicar la diferencia. */
    passThroughAmount: number;
}

/**
 * Las dos lecturas del margen de un estimado, listas para pintar.
 *
 * `differs` compara los números YA REDONDEADOS, no los crudos: si los dos se
 * escriben "30,0%", enseñar los dos es ruido aunque por dentro difieran en la
 * quinta cifra.
 */
export function marginPairOf(totals: Pick<EstimateTotals,
    'marginPercent' | 'marginPercentOwnWork' | 'passThroughRevenue'>): MarginPairText {
    const own = formatMarginPercent(totals.marginPercentOwnWork);
    const billed = formatMarginPercent(totals.marginPercent);
    return {
        own,
        billed,
        differs: own !== billed,
        passThroughAmount: Number.isFinite(totals.passThroughRevenue) ? totals.passThroughRevenue : 0,
    };
}
